import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useTitle } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useSettingsStore } from '../../../store/useSettingsStore';
import { S3Uploader } from '@/helpers/s3';
import { useItemDuplicate } from '../composables/useItemDuplicate';
import { ViewerMode } from './useViewerModeStore';

export enum ItemType {
  VIDEO = 'video',
  IMAGE = 'image',
  PDF = 'pdf',
  AUDIO = 'audio',
  OTHER = 'other',
}
export enum ActionType {
  ANNOTATED_VIDEO = 'av',
  VIDEO = 'vi',
  GIF = 'gi', // GIF (6.x and below)
  TRANSCODED_GIF = 'gt', // Video sent to the server than is then transcoded and displayed as a gif (7.x and above)
  ANNOTATED_GIF = 'ag',
  BOOKMARK = 'bo',
  AUDIO = 'au',
  ANNOTATED_SCREENSHOT = 'as',
  SCREENSHOT = 'sc',
  FILE_UPLOAD = 'fu',
  MEETING_RECORDING = 'mt',
}
export enum ItemStatus {
  COMPLETED = 'completed',
  EDITING = 'editing',
  INCOMPLETE = 'incomplete',
}
export enum ItemVideoTranscodingStatus {
  INIT = 'init',
  SUCCEEDED = 'succeeded',
  ENQUEUED = 'enqueued',
  TRANSCODING = 'transcoding',
  FAILED = 'failed',
}

export enum ItemSharingMode {
  UNRESTRICTED = 'un',
  PASSWORD = 'pw',
  EMAIL = 'em',

  // organization view/edit - anyone in the org can view/edit, and it will show up in their client
  ORGANIZATION_VIEW = 'og',
  ORGANIZATION_EDIT = 'oe',

  // specific_users - any *users* with certain emails can view, provided they have the link
  SPECIFIC_USER_VIEW = 'su',
  SPECIFIC_USER_EDIT = 'se',
}

export enum ItemExpiresMode {
  NEVER = 'ne',
  SETUP = '',
}

export enum DisabledReason {
  ADMIN = 'admin',
  BANDWIDTH = 'bandwidth',
  OTHER = 'other',
  REPLY_LIMIT = 'reply_limit',
}

export interface ItemSecurity {
  expired: boolean;
  expires: ItemExpiresMode;
  expiresAfterViews: number;
  expiresAt: string;
  password: string;
  passwordProtected: boolean;
  sharing: ItemSharingMode;
  specificUsers: string[];
}

export interface MeetingAttendee {
  email: string;
  name?: string | null;
  responseStatus?: string | null;
  isOrganizer?: boolean;
  isInternal?: boolean;
}

// Serializer strips organizerEmail/recordingUrl/attendees for non-owners and
// build_meta compacts nils, so every field is optional.
export interface MeetingInfo {
  recallBotId?: string;
  recallRecordingId?: string;
  calendarEventId?: string;
  title?: string;
  meetingUrl?: string;
  startsAt?: string;
  endsAt?: string;
  organizerEmail?: string;
  recordingUrl?: string;
  attendees?: MeetingAttendee[];
}

export interface Item {
  id: string;
  // @todo i think we should make flat store structure - split it in initializer or add some kind of translator backend<=>frontend
  relationships: {
    callToAction: {
      data: {
        id: string;
      };
    };
    user?: {
      data: {
        id: string;
      };
    };
  };
  transcodeStatus: string;
  attributes: {
    name?: string;
    allowAnonymousComments: boolean;
    commentsDisabled: boolean | null;
    downloadable: boolean;
    downloadEnabled: boolean;
    favorite: boolean;
    itemType: ItemType;
    hasAudio?: boolean;
    actionType: ActionType;
    fileExt: string;
    transcodeStatus: string;
    isHls: boolean;
    nameWithoutExtension: string;
    description: string;
    reactionsDisabled: boolean;
    inactive: boolean;
    viewLimitReached: boolean;
    disabledReason: DisabledReason | null;
    status: string;
    createdAt: string;
    updatedAt: string;
    uploading: boolean;
    instantVideo: boolean;
    security?: ItemSecurity;
    publicVisitsCount: number;
    requestContentId: string;
    requestContentQueryParams?: Object;
    thumbnailTimestamp?: number;
    viewerLayout?: ViewerMode | null;
    info?: {
      // Seconds, serialized as strings from the item's `meta`.
      duration?: string;
      transcodedDuration?: string;
      formattedDuration?: string;
      meeting?: MeetingInfo;
    };
  };
  links: {
    annotateUrl?: string;
    contentUrl: string;
    captionsUrl?: string;
    downloadUrl?: string;
    hlsUrl?: string;
    masterPlaylistUrl?: string;
    posterUrl?: string;
    shareUrl?: string;
    thumbnailUrl?: string;
    viewerUrl?: string;
  };
  type: 'string';
}

export interface ItemCollectionEntity {
  name: string;
  url: string;
}

export interface ItemCollection {
  next?: ItemCollectionEntity;
  prev?: ItemCollectionEntity;
  current?: ItemCollectionEntity;
}

export interface ItemOrganization {
  id: string;
  free: boolean;
  commentsDisabled: boolean; // getting null from backend
  logo?: string;
  hideCloudappBranding: boolean;
  redirectRootUrl?: string;
  reactionsDisabled?: boolean; // getting null from backend
}

export interface ItemShareLinks {
  gmail: string;
  pinterest: string;
  facebook: string;
  twitter: string;
  mail: string;
  linkedin: string;
}
export interface ItemState {
  videoRuntime: {
    seekTime?: number; // Time to seek to (renamed from currentTime)
    /** When true, the player should call .play() after the pending seek. Used by log click-to-seek. */
    seekShouldPlay?: boolean;
    /** Current playback position in seconds, pushed from useVideoJs.onTimeUpdate. */
    currentPlaybackTime: number;
    initialTime: number; // Initial start time from URL parameter
    isPlaying: boolean;
    newContentUrl?: string;
    playerOptions: {
      volume: number;
      playbackRate: number;
      captionsLanguage: string;
    };
  };
  item?: Item;
  itemCollection?: ItemCollection; // used for navigation betwean collections
  itemOrganization?: ItemOrganization;
  itemShareLinks?: ItemShareLinks;
  annotate: {
    image?: string;
    isSaving?: boolean;
    saveProgress: number;
    enabled: boolean;
  };
  thumbnailCapture: {
    isCapturing: boolean;
    lastCapturedTime: number;
  };
  isCopying: boolean;
}

const { debug } = useDebug(DEBUG_NAMESPACE.VIEWER_ITEM);

export const useViewerItemStore = defineStore('viewerItemStore', {
  state: () =>
    ({
      item: undefined,
      itemCollection: undefined,
      itemOrganization: undefined,
      itemShareLinks: undefined,
      videoRuntime: {
        seekTime: undefined, // Renamed from currentTime
        currentPlaybackTime: 0,
        initialTime: 0, // Initial start time from URL parameter
        isPlaying: false,
        // we don't want to update item contentURL during playback
        // it is used to update item contentUrl after video has ended
        newContentUrl: undefined,
        playerOptions: {
          volume: 1,
          playbackRate: 1,
          captionsLanguage: '',
        },
      },
      annotate: {
        image: undefined,
        saveProgress: 0,
        enabled: false,
      },
      thumbnailCapture: {
        isCapturing: false,
        lastCapturedTime: 0,
      },
      isCopying: false,
    }) as ItemState,
  actions: {
    // Initialize viewer from application store initializer
    initialize(data: any) {
      this.setItem(data.item);
      this.setItemOrganization(data.itemOrganization);
      this.setItemCollection(data.itemCollection);
      this.setItemSocialShare(data.itemShareLinks);

      debug('[initialize] Initialize viewer item store', data);
    },
    /**
     * [SW-3059]
     * Load item after viewer loaded only if item is not incomplete
     * because there is a chance that pusher event (to update item) will be triggered before viewer is loaded
     * @returns
     */
    async preloadPendingItem() {
      if (!this.id) {
        return;
      }

      if (this.status !== ItemStatus.INCOMPLETE) {
        return;
      }

      await Api.item.get(this.id).then(response => this.initialize(response.data));
    },
    // Set initial time from URL parameter (called from component with parsed time)
    setInitialTime(time: number) {
      const initialTime = Math.max(0, time);
      this.videoRuntime.initialTime = initialTime;
      debug('[setInitialTime] Set initial time:', initialTime);
    },
    onPusherUpdate(item: any) {
      debug('[onPusherUpdate] - data: ', item);

      if (item?.links?.contentUrl) {
        // Backend returns wrong version in content url (with old version provided)
        // Add param to avoid cache ie in annotations
        const url = new URL(item?.links?.contentUrl);
        url.searchParams.set('v', `v-${Date.now()}`);
        this.setContentUrl(url.toString());
      }

      if (item?.attributes?.nameWithoutExtension) {
        const title = useTitle();
        title.value = item?.attributes?.nameWithoutExtension;
      }

      if (item.attributes) {
        this.setItem(item);
      }
    },
    setItem(data: any) {
      // during playback we don't want to reload the video item
      if (this.videoRuntime.isPlaying) {
        debug('[setItem] video is playing, skipping item contentUrl update', data?.links?.contentUrl);
        this.videoRuntime.newContentUrl = data?.links?.contentUrl;

        data = {
          ...data,
          links: {
            ...data.links,
            contentUrl: this.contentUrl,
          },
        };
      }

      debug('[setItem] $patch ', data);
      this.$patch({ item: data });
    },
    setItemOrganization(data: any) {
      if (!data) {
        return;
      }

      this.$patch({
        itemOrganization: {
          id: data.id,
          free: data.attributes.free,
          ...data.attributes.preferences,
        },
      });
    },
    setItemCollection(data: any) {
      this.itemCollection = data;
    },
    setItemSocialShare(data: ItemShareLinks) {
      this.itemShareLinks = data;
    },
    clearSeekTime() {
      debug('[clearSeekTime] Clear seek time');
      this.videoRuntime.seekTime = undefined;
      this.videoRuntime.seekShouldPlay = undefined;
    },
    /**
     * Request the player to seek to `time`. Pass `{ autoplay: true }` to also
     * start playing from the new position (useful for click-to-seek from
     * action lists like logs or chapters where the user wants to see the event).
     */
    setVideoTime(time: number, options: { autoplay?: boolean } = {}) {
      const autoplay = options.autoplay === true;
      debug('[setVideoTime] Seek to time:', time, 'autoplay:', autoplay);
      this.videoRuntime.seekTime = time;
      this.videoRuntime.seekShouldPlay = autoplay;
    },
    /**
     * Pushed from useVideoJs.onTimeUpdate on every video timeupdate / seeked event.
     * Components that need to react to playback progress (chapters, logs auto-scroll)
     * should read `currentPlaybackTime` via storeToRefs instead of listening to
     * the DOM directly.
     */
    setCurrentPlaybackTime(time: number) {
      this.videoRuntime.currentPlaybackTime = time;
    },
    setVideoRuntimePlaying(val: boolean) {
      debug('[setVideoRuntimePlaying] - ', val);
      this.videoRuntime.isPlaying = val;

      if (!val && this.videoRuntime.newContentUrl) {
        this.setContentUrl(this.videoRuntime.newContentUrl);
        this.videoRuntime.newContentUrl = undefined;
      }
    },
    storeVideoPlayerOptions(options: { volume?: number; playbackRate?: number; captionsLanguage?: string }) {
      debug('[storeVideoPlayerOptions] $patch ', options);
      this.$patch({ videoRuntime: { playerOptions: { ...this.videoRuntime.playerOptions, ...options } } });
    },
    setThumbnailCapturing(capturing: boolean) {
      debug('[setThumbnailCapturing] - ', capturing);
      this.thumbnailCapture.isCapturing = capturing;
    },
    captureThumbnail(timestamp: number) {
      debug('[captureThumbnail] - timestamp:', timestamp);
      this.thumbnailCapture.lastCapturedTime = timestamp;
      // This will be connected to the API call in useItemActions
    },
    updateThumbnailTimestamp(timestamp: number) {
      if (!this.item) {
        return Promise.reject(new Error('Cannot set thumbnail timestamp. Empty item'));
      }
      this.item.attributes.thumbnailTimestamp = timestamp;
      return Api.item.save(this.item.id, { thumbnail_timestamp: timestamp });
    },
    setDisplayName(name: string) {
      if (!this.item) {
        return;
      }

      this.item.attributes.nameWithoutExtension = name;
    },
    updateDisplayName(name: string) {
      if (!this.item) {
        return Promise.reject(new Error('Cannot set name. Empty item'));
      }

      this.item.attributes.nameWithoutExtension = name;

      return Api.item.save(this.item.id, { name: `${name}${this.item.attributes.fileExt}` });
    },
    updateDescription(description: string) {
      if (!this.item) {
        return Promise.reject(new Error('Cannot set name. Empty item'));
      }

      this.item.attributes.description = description;

      return Api.item.save(this.item.id, { description });
    },
    async updateViewerLayout(viewerLayout: ViewerMode) {
      if (!this.item) {
        return Promise.reject(new Error('Cannot set viewer layout. Empty item'));
      }

      const previousViewerLayout = this.item.attributes.viewerLayout;
      this.item.attributes.viewerLayout = viewerLayout;
      try {
        const response = await Api.item.save(this.item.id, { viewerLayout });
        // An older request can resolve after a newer optimistic selection.
        // Only hydrate its response while it still represents the active choice.
        if (response.data.item && this.item.attributes.viewerLayout === viewerLayout) {
          this.setItem(response.data.item);
        }
        return response;
      } catch (error) {
        // Do not overwrite a later optimistic selection while this request was in flight.
        if (this.item.attributes.viewerLayout === viewerLayout) {
          this.item.attributes.viewerLayout = previousViewerLayout;
        }
        throw error;
      }
    },
    setContentUrl(url: any) {
      if (!this.item) {
        return;
      }

      // during playback we don't want to reload the item
      if (this.videoRuntime.isPlaying) {
        debug('[setContentUrl] video is playing, scheduling contentUrl update', url);
        this.videoRuntime.newContentUrl = url;
        return;
      }

      debug('[setContentUrl] ', url);

      this.item.links.contentUrl = url;
    },
    setDescription(desc: string) {
      if (!this.item) {
        return;
      }

      this.item.attributes.description = desc;
    },
    setItemType(type: ItemType) {
      if (!this.item) {
        return;
      }

      this.item.attributes.itemType = type;
    },
    setItemStatus(status: ItemStatus) {
      if (!this.item) {
        return;
      }

      this.item.attributes.status = status;
    },
    async copyCurrentItem({ duplicate }: { duplicate?: boolean }) {
      if (!this.item?.id) {
        return Promise.reject();
      }

      this.isCopying = true;

      try {
        const { handleDuplicated } = useItemDuplicate();
        const res = await Api.item.copy(this.item.id);
        handleDuplicated({
          redirectUrl: res.data?.item?.links?.viewerUrl,
          storageKey: res.data?.item?.id,
        });
        // Don't reset isCopying — page is navigating away via handleDuplicated
      } catch (e) {
        debug('Cannot copy item', e);
        this.isCopying = false;
        throw e;
      }
    },
    async trackCustomDomainView() {
      let trackingAllowed = true;
      const settingsStore = useSettingsStore();
      const { isCustomDomain, baseUrl } = storeToRefs(settingsStore);
      const route = useRoute();

      if (!baseUrl?.value || !baseUrl?.value.length) {
        return;
      }

      if (route.query?.sv) {
        Api.viewTrack.disallow(baseUrl.value);
        trackingAllowed = false;
      } else {
        await Api.viewTrack
          .show(baseUrl.value)
          .then(result => {
            trackingAllowed = result?.trackingAllowed || false;
          })
          .catch(e => debug('Cannot track view', e));
      }

      if (trackingAllowed && isCustomDomain?.value) {
        const email = route.query.email;

        await Api.viewTrack.view(baseUrl.value, { item: this.id, email }).catch(e => debug('Cannot track view', e));
      }
    },
    setAnnotateProgress(progress: number) {
      this.annotate.saveProgress = progress;
    },
    setAnnotateImage(image?: string) {
      this.annotate.image = image;
    },
    setIsAnnotated(val: boolean) {
      this.annotate.enabled = val;
    },
    moveToTrash() {
      if (!this.id) {
        return Promise.reject('Item id not specified');
      }

      return Api.item.delete(this.id);
    },
    async saveAnnotatedImage(fileData: string) {
      if (!this.id) {
        return;
      }

      this.annotate.isSaving = true;
      this.setAnnotateImage(fileData);

      const savedData = await Api.item.save(this.id, { overwrite: true });
      const file = savedData.data.item.attributes;

      const s3Response = await S3Uploader({
        url: file.url,
        file: fileData,
        S3Data: file.s3,
        ext: file.fileExt,
        name: file.name,
      });

      const data: any = s3Response;
      const key = data['Key'];
      const checksum = data['ETag'];

      return Api.item
        .complete(this.id, { key, checksum, web_annotate: true })
        .then(response => this.setItem(response.data.item))
        .then(() => this.setAnnotateImage())
        .catch(e => debug('[saveAnnotatedImage] error:', e))
        .finally(() => {
          this.setAnnotateProgress(0);
          this.setIsAnnotated(false);
          this.annotate.isSaving = false;
        });
    },
    saveSecurity(payload: { sharing?: ItemSharingMode; emailShares?: string[]; emailSharesChanged?: boolean; password?: string }) {
      if (!this.id) {
        return Promise.reject('Missing id');
      }

      const res = Api.item.save(this.id, payload);
      res.then(response => this.setItem(response.data.item));
      return res;
    },
    async toggleFavorite(id?: string) {
      if (!id) {
        return Promise.reject('Missing id');
      }

      const res = await Api.item.favorite.put(id);
      // API returns array of items, but we only need one
      if (res.data.items.length === 1) {
        this.setItem(res.data.items[0]);
      }
      return res;
    },
    async toggleFeatureDisabled(id: string | undefined, feature: { comments_disabled?: boolean; reactions_disabled?: boolean }) {
      if (!id) {
        return Promise.reject('Missing id');
      }

      const res = await Api.item.save(id, feature);
      if (res.data.item) {
        this.setItem(res.data.item);
      }
      return res;
    },
    saveExpiration(payload: { expires?: ItemExpiresMode; expiresAfterViews?: number | null; expiresAt?: string | null }) {
      if (!this.id) {
        return Promise.reject('Missing id');
      }

      const { expiresAt, expiresAfterViews } = payload;
      if (expiresAfterViews === null && typeof expiresAt === 'undefined' && !this.expiresAt) {
        debug('saveExpiration:disable by views');
        payload.expires = ItemExpiresMode.NEVER;
        delete payload.expiresAt;
        delete payload.expiresAfterViews;
      }

      if (expiresAt === null && typeof expiresAfterViews === 'undefined' && !this.expiresAfterViews) {
        debug('saveExpiration:disable by date');
        payload.expires = ItemExpiresMode.NEVER;
        delete payload.expiresAt;
        delete payload.expiresAfterViews;
      }

      if (expiresAt || expiresAfterViews) {
        debug('saveExpiration:enable feature');
        payload.expires = ItemExpiresMode.SETUP;
      }

      return Api.item.save(this.id, payload).then(response => this.setItem(response.data.item));
    },
  },
  getters: {
    id: (state: ItemState) => state.item?.id,
    ctaId: (state: ItemState) => state.item?.relationships?.callToAction?.data?.id,
    socialLinks: (state: ItemState) => state.itemShareLinks,
    shareLink: (state: ItemState) => state.item?.links?.shareUrl,
    downloadEnabled: (state: ItemState) => state.item?.attributes?.downloadEnabled && state?.item?.attributes?.downloadable,
    downloadLink: (state: ItemState) => state.item?.links?.downloadUrl,
    thumbnailUrl: (state: ItemState) => state.item?.links?.thumbnailUrl,
    posterUrl: (state: ItemState) => state.item?.links?.posterUrl,
    favorite: (state: ItemState) => state.item?.attributes?.favorite,
    allowAnonymousComments: (state: ItemState) => state.item?.attributes?.allowAnonymousComments,
    commentsEnabled: (state: ItemState) => !state.item?.attributes?.commentsDisabled && !state.itemOrganization?.commentsDisabled,
    commentsDisabled: (state: ItemState) => state.item?.attributes?.commentsDisabled || state.itemOrganization?.commentsDisabled,
    orgCommentsDisabled: (state: ItemState) => !!state.itemOrganization?.commentsDisabled,
    orgReactionsDisabled: (state: ItemState) => !!state.itemOrganization?.reactionsDisabled,
    type: (state: ItemState) => state.item?.attributes?.itemType,
    actionType: (state: ItemState) => state.item?.attributes?.actionType,
    isMeeting: (state: ItemState) => state.item?.attributes?.actionType === ActionType.MEETING_RECORDING,
    meetingInfo: (state: ItemState) => state.item?.attributes?.info?.meeting,
    meetingAttendees: (state: ItemState) => state.item?.attributes?.info?.meeting?.attendees ?? [],
    collectionNextItem: (state: ItemState) => state.itemCollection?.next,
    collectionPrevItem: (state: ItemState) => state.itemCollection?.prev,
    collectionCurrentItem: (state: ItemState) => state.itemCollection?.current,
    fileExt: (state: ItemState) => state.item?.attributes?.fileExt,
    contentUrl: (state: ItemState) => state.item?.links?.contentUrl,
    displayName: (state: ItemState) => state.item?.attributes?.nameWithoutExtension,
    description: (state: ItemState) => state.item?.attributes?.description,
    isGif: (state: ItemState) => state.item?.attributes?.fileExt?.includes('gif'),
    isVideoHls: (state: ItemState) => state.item?.attributes?.isHls,
    seekVideoTime: (state: ItemState) => state.videoRuntime.seekTime,
    seekShouldPlay: (state: ItemState) => state.videoRuntime.seekShouldPlay === true,
    currentPlaybackTime: (state: ItemState) => state.videoRuntime.currentPlaybackTime,
    initialVideoTime: (state: ItemState) => state.videoRuntime.initialTime,
    currentVideoIsPlaying: (state: ItemState) => state.videoRuntime.isPlaying,
    storedPlayerCaptionsLanguage: (state: ItemState) => state.videoRuntime.playerOptions.captionsLanguage,
    storedPlayerOptions: (state: ItemState) => state.videoRuntime.playerOptions,
    videoHlsSrc: (state: ItemState) => state.item?.links?.hlsUrl,
    videoCaptions: (state: ItemState) => state.item?.links?.captionsUrl,
    videoTranscodeStatus: (state: ItemState) => state.item?.attributes?.transcodeStatus,
    hasAudioStream: (state: ItemState) => state.item?.attributes?.itemType === ItemType.VIDEO && state.item?.attributes?.hasAudio !== false,
    status: (state: ItemState) => state?.item?.attributes?.status,
    createdAt: (state: ItemState) => state?.item?.attributes?.createdAt,
    updatedAt: (state: ItemState) => state?.item?.attributes?.updatedAt,
    uploading: (state: ItemState) => state?.item?.attributes?.uploading,
    instantVideo: (state: ItemState) => state?.item?.attributes?.instantVideo,
    viewerLayout: (state: ItemState) => state.item?.attributes?.viewerLayout,
    reactionsDisabled: (state: ItemState) => state.itemOrganization?.reactionsDisabled || state?.item?.attributes?.reactionsDisabled,
    inactive: (state: ItemState) => state.item?.attributes?.inactive,
    disabledReason: (state: ItemState) => state.item?.attributes?.disabledReason ?? null,
    hiddenByReplyLimit: (state: ItemState) => state.item?.attributes?.disabledReason === DisabledReason.REPLY_LIMIT,
    limitReached: (state: ItemState) =>
      state.item?.attributes?.viewLimitReached || state.item?.attributes?.inactive || state.item?.attributes?.disabledReason === DisabledReason.REPLY_LIMIT,
    hideBranding: (state: ItemState) => state.itemOrganization?.hideCloudappBranding,
    userId: (state: ItemState) => state.item?.relationships?.user?.data.id,
    password: (state: ItemState) => state.item?.attributes?.security?.password,
    requestContentId: (state: ItemState) => state.item?.attributes?.requestContentId,
    hasRequestContentResponse: (state: ItemState) => !!state.item?.attributes?.requestContentId,
    requestContentQueryParams: (state: ItemState) => state.item?.attributes?.requestContentQueryParams,
    isEditEditing: (state: ItemState) => {
      const isVideo = state.item?.attributes?.itemType === ItemType.VIDEO;
      return state.annotate.isSaving || (isVideo && state.item?.attributes?.status === ItemStatus.EDITING);
    },
    isEditProcessing: (state: ItemState) => {
      const isVideo = state.item?.attributes?.itemType === ItemType.VIDEO;

      return (
        state.annotate.isSaving ||
        state.item?.attributes?.uploading ||
        (isVideo &&
          (state.item?.attributes?.status === ItemStatus.EDITING ||
            state.item?.attributes?.status === ItemStatus.INCOMPLETE ||
            (state.item?.attributes?.instantVideo && state.item?.attributes?.transcodeStatus === ItemVideoTranscodingStatus.INIT) ||
            state.item?.attributes?.transcodeStatus === ItemVideoTranscodingStatus.ENQUEUED ||
            state.item?.attributes?.transcodeStatus === ItemVideoTranscodingStatus.TRANSCODING))
      );
    },
    canViewItem: (state: ItemState) => {
      // If the item is currently being annotated or uploaded, it cannot be viewed
      if (state.annotate.isSaving || state.item?.attributes?.uploading) {
        return false;
      }

      // Check if the item is a video
      const isVideo = state.item?.attributes?.itemType === ItemType.VIDEO;

      // If the item is not a video, it can be viewed
      if (!isVideo) {
        return true;
      }

      // If the video is an instant video, it can be viewed
      if (state.item?.attributes?.instantVideo) {
        return true;
      }

      // If the video is currently being edited or is incomplete, it cannot be viewed
      if (state.item?.attributes?.status === ItemStatus.EDITING || state.item?.attributes?.status === ItemStatus.INCOMPLETE) {
        return false;
      }

      // If none of the above conditions are met, the item can be viewed
      return true;
    },
    sharing: (state: ItemState) => state.item?.attributes?.security?.sharing,
    sharingSpecificUsers: (state: ItemState) => state?.item?.attributes?.security?.specificUsers,
    sharingInEmailSharingMode() {
      const sharing = this.sharing;
      switch (sharing) {
        case ItemSharingMode.SPECIFIC_USER_VIEW:
        case ItemSharingMode.SPECIFIC_USER_EDIT:
          return true;
      }
      return false;
    },
    sharingModeIcon() {
      switch (this.sharing) {
        case ItemSharingMode.UNRESTRICTED:
          return 'general.globe';
        case ItemSharingMode.SPECIFIC_USER_VIEW:
          return 'general.users';
        case ItemSharingMode.ORGANIZATION_VIEW:
          return 'general.organization';
        case ItemSharingMode.ORGANIZATION_EDIT:
          return 'general.organizationWithEdit';
        case ItemSharingMode.PASSWORD:
          return 'general.shieldKey';
        case ItemSharingMode.EMAIL:
          return 'general.shieldEmail';
        default:
          return 'general.share';
      }
    },
    expired: (state: ItemState) => state.item?.attributes?.security?.expired,
    expires: (state: ItemState) => state.item?.attributes?.security?.expires,
    expiresAt: (state: ItemState) => state.item?.attributes?.security?.expiresAt,
    expiresAfterViews: (state: ItemState) => state.item?.attributes?.security?.expiresAfterViews,
    embeddingAllowed(): boolean {
      return this.type === ItemType.IMAGE || this.type === ItemType.VIDEO;
    },
    publicVisitsCount: (state: ItemState) => state.item?.attributes?.publicVisitsCount,
    getTimestampedShareLink:
      (state: ItemState) =>
      (startTime: number): string => {
        const baseUrl = state.item?.links?.shareUrl || window.location.href;
        const url = new URL(baseUrl);

        // Preserve current URL query parameters and hash
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.forEach((value, key) => {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        });

        const t = Math.floor(startTime);
        if (t) {
          url.searchParams.set('t', t.toString());
        } else {
          url.searchParams.delete('t');
        }

        return url.toString();
      },
  },
  persist: {
    paths: ['videoRuntime.playerOptions'],
  },
});
